import { useState, useEffect } from "react";
import { CreditCard, X } from "lucide-react";
import { useLoaderData, useRevalidator } from "react-router-dom";
import { toast } from "react-toastify";
import { getToken } from "../../utils/token";
import instance from "../../axios/instance";
import {
  CardAddressInput,
  CardCVVInput,
  CardExpiryInput,
  CardNameInput,
  CardNumberInput,
  CardzipInput,
} from "../inputs/CreditCardForm";
import Spinner from "../spinners/Spinner";
import {
  detectCardType,
  formatCardNumber,
  formatExpiryDate,
  validateCardNumber,
  validateCVV,
  validateExpiryDate,
} from "../../utils/cardUtils";

export default function CardInfoModal(props) {
  const loaderData = useLoaderData() || {};
  const [loading, setLoading] = useState(false);
  const [cardData, setCardData] = useState({
    number: "",
    expiry: "",
    cvv: "",
    name: props.name || "",
    address: props.address || "",
    zip: props.zipcode || "",
  });

  const [errors, setErrors] = useState({});

  const btnDisabled =
    !cardData.name ||
    !cardData.address ||
    !cardData.zip ||
    !cardData.cvv ||
    !cardData.expiry ||
    !cardData.number;

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    const token = getToken();

    const newErrors = {};
    Object.keys(cardData).forEach((key) => {
      const field = key;
      const error = validateField(field, cardData[field]);
      if (error) newErrors[field] = error;
    });

    setErrors(newErrors);
    setLoading(true);

    if (Object.keys(newErrors).length === 0) {
      try {
        const endpointUrl =
          props.type === "clinic"
            ? "/doctor/facility/card/update"
            : "/doctor/card/update";
        await instance.put(
          endpointUrl,
          { ...cardData, id: props.facilityId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          },
        );
        setLoading(false);
        toast.success("Card information updated");

        setCardData({
          name: "",
          number: "",
          cvv: "",
          expiry: "",
          address: "",
          zip: "",
        });
        props.onClose();
      } catch (error) {
        console.error(error.response?.data);
        toast.error(error.response?.data?.message || "An error occurred");
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
  };

  const cardType = detectCardType(cardData.number);
  const validateField = (field, value) => {
    switch (field) {
      case "number":
        return validateCardNumber(value) ? "" : "Invalid card number";
      case "expiry":
        return validateExpiryDate(value) ? "" : "Invalid expiry date";
      case "cvv":
        return validateCVV(value, cardType) ? "" : "Invalid CVV";
      case "name":
        return value.trim().length >= 2
          ? ""
          : "Name must be at least 2 characters";
      case "address":
        return value.trim().length >= 2
          ? ""
          : "Address must be at least 2 characters";
      case "zip":
        return value.trim().length >= 3
          ? ""
          : "Zip code must be at least 3 characters";
      default:
        return "";
    }
  };

  const handleInputChange = (field, value) => {
    let formattedValue = value;

    if (field === "number") {
      formattedValue = formatCardNumber(value);
    } else if (field === "expiry") {
      formattedValue = formatExpiryDate(value);
    } else if (field === "cvv") {
      formattedValue = value
        .replace(/\D/g, "")
        .slice(0, cardType === "amex" ? 4 : 3);
    } else if (field === "name") {
      formattedValue = value.replace(/[^a-zA-Z\s]/g, "").slice(0, 50);
    }

    setCardData((prev) => ({ ...prev, [field]: formattedValue }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleBlur = (field) => {
    const error = validateField(field, cardData[field]);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const isOpen = props.open || props.isOpen;
  if (!isOpen) return null;

  return (
    <div className="fixed top-0 z-[1000] bottom-0 right-0 left-0 bg-[#11111180] flex justify-center items-center p-4">
      <div className="bg-white rounded-xl max-w-[550px] w-full h-fit max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="p-4 flex sticky top-0 left-0 right-0 z-20 bg-white rounded-t-xl justify-between items-center border-b">
          <div className="font-medium text-[15px]">Card Details</div>
          <button
            onClick={props.onClose}
            type="button"
            className="text-gray-500 hover:text-black"
          >
            <X size={22} />
          </button>
        </div>

        <form onSubmit={handleFormSubmit} className="pt-3 pb-6 px-4 space-y-5">
          <CardNumberInput
            handleBlur={handleBlur}
            handleInputChange={handleInputChange}
            cardData={cardData}
            errors={errors}
            cardType={cardType}
          />
          <CardNameInput
            handleBlur={handleBlur}
            handleInputChange={handleInputChange}
            cardData={cardData}
            errors={errors}
          />
          <div className="grid grid-cols-2 gap-x-4">
            <CardExpiryInput
              handleBlur={handleBlur}
              handleInputChange={handleInputChange}
              cardData={cardData}
              errors={errors}
            />
            <CardCVVInput
              handleBlur={handleBlur}
              handleInputChange={handleInputChange}
              cardData={cardData}
              errors={errors}
              cardType={cardType}
            />
          </div>
          <div className="grid grid-cols-[auto_30%] gap-x-4">
            <CardAddressInput
              handleBlur={handleBlur}
              handleInputChange={handleInputChange}
              cardData={cardData}
              errors={errors}
            />
            <CardzipInput
              handleBlur={handleBlur}
              handleInputChange={handleInputChange}
              cardData={cardData}
              errors={errors}
            />
          </div>
          <div className="col-span-2 flex">
            <button
              disabled={btnDisabled || loading}
              type="submit"
              className="flex gap-x-2 items-center justify-center text-white bg-primary p-3 font-medium text-[14px] rounded-md disabled:opacity-60 disabled:cursor-not-allowed shadow-sm w-full"
            >
              {loading ? <Spinner color="white" /> : <CreditCard size={18} />}{" "}
              Save card details
            </button>
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500 pt-1">
            <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
              <svg
                className="w-2 h-2 text-white"
                fill="currentColor"
                viewBox="0 0 8 8"
              >
                <path d="M6.564.75l-3.59 3.612-1.538-1.55L0 4.26l2.974 2.99L8 2.193z" />
              </svg>
            </div>
            Your card is safely encrypted when saved
          </div>
        </form>
      </div>
    </div>
  );
}
